import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { CustomDatesCache } from "@/utils/tree-generation/custom-date-utils";
import { createArea4dSessionNodeIfNeeded } from "@/utils/tree-generation/generate-cluster-tree";
import { elementToTreeData, TreeData } from "@/utils/tree-generation/tree-data";
import { isElementNodeToHide } from "@/utils/tree-generation/tree-discriminators";
import type { AreaDataSets } from "@/utils/tree-generation/tree-filters-utils";
import {
  GENERIC_DATASET_TYPE_HINTS,
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import {
  IElementsRecord,
  type ClustersRecord,
} from "@faro-lotv/project-source";
import { partition } from "es-toolkit";
import { reportFilter } from "./create-report-tree-filter";

type GenerateProjectTreeParams = {
  /** ID of the parent of the current recursion level */
  parentId: GUID;

  /** All IElements of the project */
  elements: LightweightElementsRecord | IElementsRecord;

  /** The capture-tree datasets contained in each area @default {} */
  areaDataSets?: AreaDataSets;

  /** The main revision tree used to build the clusters structure @default {} */
  clusters?: ClustersRecord;

  /** Id of the tag identifying rooms copied for video mode datasets */
  videoModeCopyTagId?: GUID;
};

const genericDatasetTypeHints = new Set<string>(
  Object.values(GENERIC_DATASET_TYPE_HINTS),
);

/**
 * @returns A representation of the project tree useful to present data for the report tool.
 */
export function generateReportTree({
  parentId,
  elements,
  videoModeCopyTagId,
  areaDataSets,
  clusters,
}: GenerateProjectTreeParams): TreeData[] {
  const customDatesCache = new CustomDatesCache();

  const tree = generateFilteredTree(
    parentId,
    elements,
    videoModeCopyTagId,
    customDatesCache,
    areaDataSets,
    clusters,
  );

  return tree;
}

/**
 * Generates the project tree with date, text, and tags filtering applied recursively
 *
 * @param parentId ID of the parent of the current recursion level
 * @param elements All IElements of the project
 * @param videoModeCopyTagId Data that stay constant during the tree generation
 * @param customDatesCache Cache for custom dates to avoid redundant calculations
 * @param areaDataSets Area datasets available for the current area
 * @param clusters Clusters available for the current area
 * @returns The filtered tree representation
 */
function generateFilteredTree(
  parentId: GUID,
  elements: IElementsRecord | LightweightElementsRecord,
  videoModeCopyTagId: GUID | undefined,
  customDatesCache: CustomDatesCache,
  areaDataSets?: AreaDataSets,
  clusters?: ClustersRecord,
): TreeData[] {
  const subtree: TreeData[] = [];

  const parentElement = elements[parentId];
  if (!parentElement) return subtree;

  // The elements of the current level of recursion.
  // Add datasets from the capture tree as children of the area
  // if they are exposing the clusters iElements.
  const childrenIds = [...(parentElement.childrenIds ?? [])];

  for (const { elementId } of areaDataSets?.[parentElement.id] ?? []) {
    const dataset = elements[elementId];
    if (
      dataset?.childrenIds?.some(
        (id) => elements[id]?.type === IElementType.cluster,
      )
    ) {
      childrenIds.push(dataset.id);
      // Calculate and cache the custom date for the dataset
      customDatesCache.findCustomDateForDataset(dataset.id, elements);
    }
  }

  subtree.push(
    ...createArea4dSessionNodeIfNeeded(parentElement, elements, {
      areaDataSets: areaDataSets ?? {},
      clusters: clusters ?? {},
      videoModeCopyTagId,
    }),
  );

  for (const elementId of childrenIds) {
    const element = elements[elementId];
    if (
      !element ||
      isElementNodeToHide(element, elements, videoModeCopyTagId)
    ) {
      continue;
    }

    subtree.push(
      ...processChildren(
        element,
        elements,
        parentElement,
        videoModeCopyTagId,
        customDatesCache,
        areaDataSets,
        clusters,
      ),
    );
  }
  return subtree;
}

function processChildren(
  element: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
  parentElement: LightweightElement | IElement,
  videoModeCopyTagId: GUID | undefined,
  customDatesCache: CustomDatesCache,
  areaDataSets?: AreaDataSets,
  clusters?: ClustersRecord,
): TreeData[] {
  const customDate = customDatesCache.customDates.get(element.id);

  const filterResult = reportFilter(
    element,
    parentElement.type,
    videoModeCopyTagId,
    customDate,
  );

  let children: TreeData[] = [];
  if (filterResult.shouldKeepChildren) {
    children = generateFilteredTree(
      element.id,
      elements,
      videoModeCopyTagId,
      customDatesCache,
      areaDataSets,
      clusters,
    );
  }

  // A group or time series should not be visible if they're empty
  if (shouldHideIfEmpty(element)) {
    filterResult.shouldKeepElement &&= children.length > 0;
  }

  if (filterResult.shouldKeepElement) {
    // Move the 4D Sessions folder in front so that the project structure
    // will always be
    // - Area
    //    - 4D Sessions
    //      - VideoMode 1
    //      - [...]
    //      - VideoMode M
    //    - Room 1
    //    - [...]
    //    - Room N
    const [pcs, rest] = partition(
      children,
      (node) =>
        !!node.element?.typeHint &&
        (node.element.typeHint === IElementTypeHint.dataSession ||
          genericDatasetTypeHints.has(node.element.typeHint)),
    );
    children = [...pcs, ...rest];

    return [
      elementToTreeData({
        element,
        parentElement,
        children,
        createdAt: customDate,
        label: filterResult.displayName ?? element.name,
      }),
    ];
  } else if (filterResult.shouldKeepChildren) {
    return children;
  }

  return [];
}

function shouldHideIfEmpty(element: LightweightElement | IElement): boolean {
  return (
    element.type === IElementType.group ||
    element.type === IElementType.timeSeries ||
    (element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.area)
  );
}
